// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import {
    Plus, Search, Filter, SortAsc, SortDesc, MoreVertical, Edit,
    Trash2, Copy, Eye, Users, Tag, Info, X,
    Grid, List, ArrowRight
} from 'lucide-react';
import { useCustomerGroups, useDeleteCustomerGroup } from 'core';
import { SkeletonSwitch } from 'skeleton';
import { LeadsGroupGridSkeleton } from 'skeleton/leads/LeadsSkeleton';
import { useNavigate, createSearchParams } from 'react-router-dom';
import ConfirmationDialog from 'components/ui/confirmationDialog';
import Pagination from 'components/ui/pagination';

import { PageContainer } from '../ui/page-container';
import AudienceHeader from './AudienceHeader';
import { ErrorState } from "components/ui/error-state";
import { SelectField } from "components/ui/select-field";

const AUDIENCE_LIST_GRID_CLASS = `grid items-center gap-x-4 gap-y-2 px-4
    grid-cols-[auto_minmax(0,1fr)_auto]
    sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]
    md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]
    xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]
    xl:gap-x-6`;

// ─── List Skeleton (matches list view rows) ───────────────────────────────────
const LeadsGroupListSkeleton = ({ count = 7 }) => (
    <div className="space-y-3">
        <div className={`${AUDIENCE_LIST_GRID_CLASS} hidden sm:grid pb-1 text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500`}>
            <span aria-hidden="true" />
            <span className="text-center">Audience Group</span>
            <span className="text-center">Leads</span>
            <span className="text-center">Status</span>
            <span className="text-center justify-self-center">Info</span>
            <span className="text-center justify-self-center">Actions</span>
        </div>
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${AUDIENCE_LIST_GRID_CLASS} py-3.5 rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 animate-pulse`}>
                <div className="w-4 h-4 rounded bg-gray-200 dark:bg-white/[0.08] flex-shrink-0" />
                <div className="h-4 rounded-lg bg-gray-200 dark:bg-white/[0.08] min-w-0" style={{ width: `${45 + (i % 4) * 12}%` }} />
                <div className="hidden sm:block justify-self-end">
                    <div className="h-4 w-7 rounded bg-gray-200 dark:bg-white/[0.08] mb-1" />
                    <div className="h-3 w-9 rounded bg-gray-100 dark:bg-white/[0.05]" />
                </div>
                <div className="hidden sm:block h-6 w-20 rounded-full bg-emerald-100 dark:bg-emerald-500/10" />
                <div className="hidden sm:block w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.05] justify-self-end" />
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.05] justify-self-end" />
                <div className="col-start-2 col-span-2 sm:hidden flex items-center gap-3">
                    <div className="h-3 w-12 rounded bg-gray-100 dark:bg-white/[0.05]" />
                    <div className="h-5 w-16 rounded-full bg-emerald-100 dark:bg-emerald-500/10" />
                </div>
            </div>
        ))}
    </div>
);

const PortalDropdown = ({ triggerRef, isOpen, onClose, items, onDelete }) => {
    const [pos, setPos] = useState(null);

    useEffect(() => {
        if (!isOpen) { setPos(null); return; }
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const MENU_H = (items.length + (onDelete ? 1 : 0)) * 42 + 20;
        const spaceBelow = window.innerHeight - rect.bottom;
        setPos(
            spaceBelow >= MENU_H
                ? { top: rect.bottom + 6, right: window.innerWidth - rect.right }
                : { bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right }
        );
    }, [isOpen, items.length, onDelete, triggerRef]);

    useEffect(() => {
        if (!pos) return;
        const close = () => onClose();
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [pos, onClose]);

    if (!isOpen || !pos) return null;

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[9990]"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
            />
            <div
                className="fixed z-[9991] min-w-[11rem] rounded-xl border border-line dark:border-white/[0.08] bg-surface dark:bg-[#1e2433] shadow-xl overflow-hidden"
                style={{
                    top: pos.top ?? 'auto',
                    bottom: pos.bottom ?? 'auto',
                    right: pos.right,
                    animation: 'fadeIn 0.12s ease both',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {items.map((item, i) => (
                    <button
                        key={i}
                        onClick={item.onClick}
                        className="w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
                    >
                        <span className="text-content-subtle dark:text-gray-600">{item.icon}</span>
                        <span className="font-medium">{item.label}</span>
                    </button>
                ))}
                {onDelete && (
                    <>
                        <div className="border-t border-line-subtle dark:border-white/[0.06]" />
                        <button
                            onClick={onDelete}
                            className="w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/[0.08] transition-colors"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span className="font-medium">Delete</span>
                        </button>
                    </>
                )}
            </div>
        </>,
        document.body
    );
};

const EmptyState = ({ onCreateSegment, hasFilters, onClearFilters }) => (
    <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.04] flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-gray-300 dark:text-gray-600" />
        </div>
        <h4 className="text-base font-semibold text-content dark:text-white mb-2">
            {hasFilters ? 'No segments match your filters' : 'No audience segments yet'}
        </h4>
        <p className="text-sm text-content-muted dark:text-gray-400 max-w-xs mb-6">
            {hasFilters
                ? 'Try adjusting your search or filter criteria.'
                : 'Create your first segment to start targeting specific groups of leads.'}
        </p>
        {hasFilters ? (
            <button
                onClick={onClearFilters}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-line dark:border-white/[0.08] text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
                Clear filters
            </button>
        ) : (
            <button
                onClick={onCreateSegment}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold shadow-md transition-all"
            >
                <Plus className="w-4 h-4" />
                Create Segment
            </button>
        )}
    </div>
);

const CustomerGroupCard = ({
    segment, isSelected, onSelect, onDuplicate,
    viewMode = 'grid',
    onShowDetail, setGroupConfirmationOpen, setSelectedGroup
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuBtnRef = useRef(null);
    const closeMenu = useCallback(() => setShowMenu(false), []);
    const navigate = useNavigate();

    const display = useMemo(() => {
        const name = segment?.name || 'Untitled Segment';
        const statusValue = segment?.status || 'ACTIVE';
        const status = statusValue.toLowerCase();
        const leadsCount = segment?.leadsCount || 0;
        const organizationId = segment?.organizationId || 'N/A';
        const createdUser = segment?.createdUser || 'N/A';
        const uuid = segment?.uuid || 'N/A';
        return { id: segment?.id, name, status, statusValue, leadsCount, organizationId, createdUser, uuid };
    }, [segment]);

    const realTimeSize = useMemo(() => display.leadsCount, [display.leadsCount]);

    const sStyle = {
        active: { pill: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20', dot: 'bg-emerald-500 animate-pulse' },
        inactive: { pill: 'bg-gray-100 dark:bg-white/[0.06] text-content-secondary dark:text-gray-400 border border-line dark:border-white/[0.08]', dot: 'bg-gray-400 dark:bg-gray-600' },
        draft: { pill: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20', dot: 'bg-amber-500 animate-pulse' },
    }[display.status] || { pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200', dot: 'bg-emerald-500 animate-pulse' };

    const navigateToLeads = () => navigate({
        pathname: '/leads',
        search: createSearchParams({ groupId: String(segment.id), page: '1' }).toString(),
    });

    const menuItems = [
        { label: 'View Details', icon: <Info className="w-3.5 h-3.5" />, onClick: (e) => { e?.stopPropagation(); onShowDetail(segment); setShowMenu(false); } },
        { label: 'Add Lead', icon: <Plus className="w-3.5 h-3.5" />, onClick: (e) => { e?.stopPropagation(); navigate(`/leads/${segment.id}/${segment.name}/new`); setShowMenu(false); } },
        { label: 'Edit Group', icon: <Edit className="w-3.5 h-3.5" />, onClick: (e) => { e?.stopPropagation(); navigate(`/leads/manage-groups/${segment.id}/${segment.name}`); setShowMenu(false); } },
        { label: 'Duplicate', icon: <Copy className="w-3.5 h-3.5" />, onClick: (e) => { e?.stopPropagation(); onDuplicate(); setShowMenu(false); } },
        { label: 'View Leads', icon: <Eye className="w-3.5 h-3.5" />, onClick: (e) => { e?.stopPropagation(); navigateToLeads(); setShowMenu(false); } },
    ];

    const StatusPill = () => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sStyle.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sStyle.dot}`} />
            {display.statusValue}
        </span>
    );

    if (viewMode === 'list') {
        return (
            <div
                onClick={navigateToLeads}
                className={`group rounded-xl border cursor-pointer transition-all duration-200 hover:shadow-sm ${isSelected
                    ? 'border-blue-400 dark:border-blue-500/60 bg-blue-50 dark:bg-blue-500/10'
                    : 'border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 hover:border-blue-200 dark:hover:border-blue-500/30 hover:bg-gray-50/50 dark:hover:bg-white/[0.02]'
                    }`}
            >
                <div className={`${AUDIENCE_LIST_GRID_CLASS} py-3.5`}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onSelect}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-line-strong dark:border-white/[0.15] text-blue-600 dark:text-blue-300 focus:ring-blue-500/30 flex-shrink-0 cursor-pointer"
                    />

                    <div className="min-w-0 text-center">
                        <p className="font-semibold text-sm text-content dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" title={display.name}>
                            {display.name}
                        </p>
                    </div>

                    <div className="hidden sm:block min-w-0 text-center" onClick={(e) => e.stopPropagation()}>
                        <p className="font-bold text-sm text-content dark:text-white tabular-nums leading-none">{realTimeSize.toLocaleString()}</p>
                        <p className="text-xs text-content-subtle dark:text-gray-500 mt-0.5">{realTimeSize === 1 ? 'lead' : 'leads'}</p>
                    </div>

                    <div className="hidden sm:flex justify-center" onClick={(e) => e.stopPropagation()}>
                        <StatusPill />
                    </div>

                    <button
                        onClick={(e) => { e.stopPropagation(); onShowDetail(segment); }}
                        title="View details"
                        className="hidden sm:flex p-2 rounded-lg text-content-subtle dark:text-gray-600 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors justify-self-center"
                    >
                        <Info className="w-4 h-4" />
                    </button>

                    <div className="justify-self-center" onClick={(e) => e.stopPropagation()}>
                        <button
                            ref={menuBtnRef}
                            onClick={(e) => { e.stopPropagation(); setShowMenu(p => !p); }}
                            title="More actions"
                            className="p-2 rounded-lg text-content-subtle dark:text-gray-600 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                            aria-label="More options"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="col-start-2 col-span-2 flex items-center gap-3 min-w-0 sm:hidden" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs font-medium text-content-muted dark:text-gray-400 whitespace-nowrap">
                            {realTimeSize.toLocaleString()} {realTimeSize === 1 ? 'lead' : 'leads'}
                        </span>
                        <StatusPill />
                    </div>

                    <PortalDropdown
                        triggerRef={menuBtnRef}
                        isOpen={showMenu}
                        onClose={closeMenu}
                        items={menuItems}
                        onDelete={(e) => { e?.stopPropagation(); setSelectedGroup(segment.id); setGroupConfirmationOpen(true); closeMenu(); }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={navigateToLeads}
            className={`group relative rounded-2xl border cursor-pointer transition-all duration-200 overflow-hidden ${isSelected
                ? 'border-blue-400 dark:border-blue-500/60 shadow-lg ring-2 ring-blue-200/50 dark:ring-blue-500/20 bg-blue-50/30 dark:bg-blue-500/5'
                : 'border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 hover:border-blue-300 dark:hover:border-blue-500/40 hover:shadow-xl hover:-translate-y-0.5'
                }`}
        >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/[0.03] group-hover:to-indigo-500/[0.03] dark:group-hover:from-blue-400/[0.05] dark:group-hover:to-indigo-400/[0.05] transition-all duration-300 pointer-events-none rounded-2xl" />
            <div className="relative p-5">
                <div className="flex items-start justify-between mb-5">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <input type="checkbox" checked={isSelected} onChange={onSelect} onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 rounded border-line-strong dark:border-white/[0.15] text-blue-600 dark:text-blue-300 focus:ring-blue-500/30 flex-shrink-0 cursor-pointer" />
                        <h4 className="font-bold text-sm text-content dark:text-white leading-tight truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{display.name}</h4>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <StatusPill />
                        <button
                            ref={menuBtnRef}
                            onClick={(e) => { e.stopPropagation(); setShowMenu(p => !p); }}
                            className="p-1.5 rounded-lg text-content-subtle dark:text-gray-600 hover:text-content-secondary dark:hover:text-content-subtle hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                            aria-label="More options"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                        <PortalDropdown
                            triggerRef={menuBtnRef}
                            isOpen={showMenu}
                            onClose={closeMenu}
                            items={menuItems}
                            onDelete={(e) => { e?.stopPropagation(); setSelectedGroup(segment.id); setGroupConfirmationOpen(true); closeMenu(); }}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-500/[0.08] border border-blue-100 dark:border-blue-500/[0.15] mb-4">
                    <div className="w-9 h-9 rounded-lg bg-surface dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-2xl font-bold text-content dark:text-white leading-none tabular-nums">{realTimeSize.toLocaleString()}</p>
                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mt-0.5">Leads</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-blue-400 dark:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>


            </div>
        </div>
    );
};

const SegmentDetailModal = ({ segment, leads, onClose, onEdit, onViewSourceLeads }) => {
    const realTimeSize = useMemo(() => {
        if (!segment.criteria || segment.criteria.length === 0 || !leads.length) return segment.size || segment.leadsCount || 0;
        return leads.filter(lead =>
            segment.criteria.every(criterion => {
                const leadValue = lead[criterion.field];
                switch (criterion.operator) {
                    case 'equals': return leadValue === criterion.value;
                    case 'greater_than': return criterion.field === 'leadScore' ? leadValue > criterion.value : new Date(leadValue) > new Date(criterion.value);
                    case 'less_than': return criterion.field === 'leadScore' ? leadValue < criterion.value : new Date(leadValue) < new Date(criterion.value);
                    case 'contains': return leadValue && leadValue.toString().toLowerCase().includes(criterion.value.toLowerCase());
                    default: return false;
                }
            })
        ).length;
    }, [segment, leads]);

    return (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-[3px] flex items-center justify-center z-50 p-4">
            <div className="bg-surface dark:bg-dark-800 border border-line dark:border-white/[0.07] rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-5 border-b border-line-subtle dark:border-white/[0.07]">
                    <div>
                        <h2 className="text-lg font-bold text-content dark:text-white">{segment.name}</h2>
                        <p className="text-sm text-content-muted dark:text-gray-400 mt-0.5">Segment Details</p>
                    </div>
                    <button aria-label="Close" onClick={onClose} className="p-2 rounded-xl text-content-subtle dark:text-gray-600 hover:text-content-secondary dark:hover:text-content-subtle hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-blue-50 dark:bg-blue-500/[0.08] border border-blue-100 dark:border-blue-500/[0.15] p-4">
                            <p className="text-2xl font-bold text-content dark:text-white tabular-nums">{realTimeSize.toLocaleString()}</p>
                            <p className="text-sm text-blue-700 dark:text-blue-400 font-medium mt-0.5">Total Leads</p>
                        </div>
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/[0.08] border border-emerald-100 dark:border-emerald-500/[0.15] p-4">
                            <p className="text-2xl font-bold text-content dark:text-white tabular-nums">{segment.usageCount || 0}</p>
                            <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium mt-0.5">Times Used</p>
                        </div>
                    </div>

                    {segment.description && (
                        <div>
                            <h3 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-2">Description</h3>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{segment.description}</p>
                        </div>
                    )}

                    {segment.criteria && segment.criteria.length > 0 && (
                        <div>
                            <h3 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-3">Criteria</h3>
                            <div className="space-y-2">
                                {segment.criteria.map((criterion, index) => (
                                    <div key={index} className="flex items-center gap-2 text-sm">
                                        {index > 0 && <span className="text-xs font-bold text-blue-600 dark:text-blue-400 w-8 flex-shrink-0">{criterion.logicalOperator || 'AND'}</span>}
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-line dark:border-white/[0.06] flex-1">
                                            <span className="font-semibold text-content dark:text-white">{criterion.field}</span>
                                            <span className="text-content-subtle dark:text-gray-600">{criterion.operator.replace('_', ' ')}</span>
                                            <span className="font-semibold text-content dark:text-white">{Array.isArray(criterion.value) ? criterion.value.join(', ') : criterion.value}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {segment.tags && segment.tags.length > 0 && (
                        <div>
                            <h3 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-3">Tags</h3>
                            <div className="flex flex-wrap gap-2">
                                {segment.tags.map((tag, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 border border-line dark:border-white/[0.08]"><Tag className="w-3 h-3" />{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {segment.expectedPerformance && (
                        <div>
                            <h3 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-3">Expected Performance</h3>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: 'Open Rate', value: ((segment.expectedPerformance.openRate || 0) * 100).toFixed(1) + '%' },
                                    { label: 'Click Rate', value: ((segment.expectedPerformance.clickRate || 0) * 100).toFixed(1) + '%' },
                                    { label: 'Conversion', value: ((segment.expectedPerformance.conversionRate || 0) * 100).toFixed(1) + '%' },
                                ].map(({ label, value }) => (
                                    <div key={label} className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-line-subtle dark:border-white/[0.05]">
                                        <p className="text-xl font-bold text-content dark:text-white">{value}</p>
                                        <p className="text-xs text-content-muted dark:text-gray-500 mt-0.5">{label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-3">Metadata</h3>
                        <div className="rounded-xl border border-line dark:border-white/[0.07] overflow-hidden">
                            {[
                                { label: 'Segment ID', value: String(segment.id) },
                                { label: 'Organization ID', value: `#${segment.organizationId || 'N/A'}` },
                                { label: 'Created By', value: `User #${segment.createdUser || 'N/A'}` },
                                { label: 'UUID', value: segment.uuid || 'N/A', mono: true, truncate: true },
                                { label: 'Status', value: segment.status || 'ACTIVE', highlight: true },
                            ].map(({ label, value, mono, truncate, highlight }, i, arr) => (
                                <div key={label} className={`flex items-center justify-between px-4 py-3 ${i < arr.length - 1 ? 'border-b border-line-subtle dark:border-white/[0.05]' : ''} ${i % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-white/[0.02]'}`}>
                                    <span className="text-xs font-semibold text-content-muted dark:text-gray-500">{label}</span>
                                    <span className={`text-xs ${mono ? 'font-mono' : 'font-semibold'} ${highlight ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-800 dark:text-gray-200'} ${truncate ? 'max-w-[200px] truncate' : ''}`} title={truncate ? value : undefined}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-line-subtle dark:border-white/[0.07] bg-gray-50/50 dark:bg-white/[0.01]">
                    <button onClick={() => onViewSourceLeads && onViewSourceLeads(segment)} className="px-4 py-2 rounded-xl border border-line dark:border-white/[0.08] text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors">
                        View Leads
                    </button>
                    <button onClick={onEdit} className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm font-semibold transition-colors shadow-sm">
                        Edit Segment
                    </button>
                </div>
            </div>
        </div>
    );
};

const EnhancedAudiencesView = ({
    segments = [],
    onCreateSegment,
    onEditSegment,
    onDeleteSegment,
    onDuplicateSegment,
    leads = [],
    onViewSourceLeads
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [serverSearch, setServerSearch] = useState('');
    const searchDebounceRef = useRef(null);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedSegments, setSelectedSegments] = useState(new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [sizeFilter, setSizeFilter] = useState('all');
    const [viewMode, setViewMode] = useState('list');
    const [selectedSegmentForDetail, setSelectedSegmentForDetail] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setServerSearch(value.trim());
            setCurrentPage(1);
        }, 350);
    };

    const {
        data: groupsData,
        isFetching: groupsLoading,
        isError: groupsError,
        error: groupsErrorObj,
        refetch: refetchGroups,
    } = useCustomerGroups({
        search: serverSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
        pageSize: PAGE_SIZE,
    });

    const customerGroups = groupsData?.data ?? [];

    const { mutate: deleteCustomerGroups } = useDeleteCustomerGroup();

    const [groupConfirmationOpen, setGroupConfirmationOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const kpiMetrics = useMemo(() => ({
        totalAudiences: groupsData?.total || 0,
        activeCount: customerGroups.filter(g => g.status?.toUpperCase() === 'ACTIVE').length || 0,
    }), [groupsData, customerGroups]);

    const filteredAndSortedSegments = useMemo(() => {
        let filtered = customerGroups;
        if (sizeFilter !== 'all') {
            const segSize = (seg) => seg.leadsCount || 0;
            filtered = filtered.filter(seg => {
                const s = segSize(seg);
                if (sizeFilter === 'small') return s < 100;
                if (sizeFilter === 'medium') return s >= 100 && s < 1000;
                if (sizeFilter === 'large') return s >= 1000;
                return true;
            });
        }
        filtered.sort((a, b) => {
            let aValue = sortBy === 'size' ? (a.leadsCount || 0) : (a.name || '').toLowerCase();
            let bValue = sortBy === 'size' ? (b.leadsCount || 0) : (b.name || '').toLowerCase();
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [customerGroups, sortBy, sortOrder, sizeFilter]);


    const handleSelectSegment = (segmentId) => {
        const newSelected = new Set(selectedSegments);
        if (newSelected.has(segmentId)) newSelected.delete(segmentId);
        else newSelected.add(segmentId);
        setSelectedSegments(newSelected);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setServerSearch('');
        setStatusFilter('all');
        setSizeFilter('all');
        setCurrentPage(1);
    };


    const activeFiltersCount = [serverSearch, statusFilter !== 'all', sizeFilter !== 'all'].filter(Boolean).length;

    return (
        <PageContainer>
            <AudienceHeader
                totalAudiences={kpiMetrics.totalAudiences}
                activeCount={kpiMetrics.activeCount}
                onCreateSegment={onCreateSegment}
                isLoading={groupsLoading}
            />

            {/* Toolbar card */}
            <div className="rounded-2xl border border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 shadow-sm mb-5 overflow-hidden">
                {/* Search + controls row */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-content-subtle dark:text-gray-500 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search segments..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-line dark:border-white/[0.10] bg-gray-50 dark:bg-dark-900/60 text-sm text-content dark:text-white placeholder:text-content-subtle dark:placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 shadow-inner transition-colors"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary dark:hover:text-gray-300">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium transition-all ${showFilters || activeFiltersCount > 0
                                ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-300 dark:border-blue-500/40 text-blue-700 dark:text-blue-400'
                                : 'border-line dark:border-white/[0.08] text-content-secondary dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
                                }`}
                        >
                            <Filter className="w-4 h-4" />
                            Filters
                            {activeFiltersCount > 0 && (
                                <span className="bg-blue-600 dark:bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">{activeFiltersCount}</span>
                            )}
                        </button>

                        <SelectField
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-line dark:border-white/[0.08] bg-surface dark:bg-dark-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors"
                        >
                            <option value="name">Name</option>
                            <option value="size">Size</option>
                        </SelectField>

                        <button
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            className="p-2 rounded-xl border border-line dark:border-white/[0.08] text-content-muted dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
                        >
                            {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                        </button>

                        <div className="flex items-center gap-0.5 p-1 rounded-xl border border-line dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02]">
                            <button onClick={() => setViewMode('grid')} aria-label="Grid view"
                                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-surface dark:bg-white/[0.08] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-content-muted dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                                <Grid className="w-4 h-4" />
                            </button>
                            <button onClick={() => setViewMode('list')} aria-label="List view"
                                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-surface dark:bg-white/[0.08] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-content-muted dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filter panel */}
                {showFilters && (
                    <div className="px-5 py-4 border-t border-line-subtle dark:border-white/[0.05] bg-gray-50 dark:bg-dark-900/40">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-2">Status</label>
                                <SelectField value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.08] bg-surface dark:bg-dark-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors">
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="draft">Draft</option>
                                </SelectField>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-2">Size</label>
                                <SelectField value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.08] bg-surface dark:bg-dark-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors">
                                    <option value="all">All Sizes</option>
                                    <option value="small">Small (&lt; 100)</option>
                                    <option value="medium">Medium (100–999)</option>
                                    <option value="large">Large (1000+)</option>
                                </SelectField>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-content-muted dark:text-gray-500">{filteredAndSortedSegments.length} of {groupsData?.total || customerGroups.length} shown</span>
                                <button onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors">Clear all</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <SkeletonSwitch isLoading={groupsLoading} skeleton={
                viewMode === 'list'
                    ? <LeadsGroupListSkeleton />
                    : <LeadsGroupGridSkeleton key={5} />
            }>
                {groupsError && !groupsLoading ? (
                    /* Without this the page rendered its empty state on a failed
                       request, telling the user they had no audiences when the
                       server had simply not answered. */
                    <ErrorState
                        bare
                        title="Couldn't load your audiences"
                        description="The request didn't complete. Your segments are safe — this is only the view."
                        error={groupsErrorObj}
                        onRetry={() => refetchGroups()}
                    />
                ) : filteredAndSortedSegments.length === 0 && !groupsLoading ? (
                    <EmptyState onCreateSegment={onCreateSegment} hasFilters={activeFiltersCount > 0} onClearFilters={clearFilters} />
                ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5' : 'space-y-3'}>
                        {viewMode === 'list' && (
                            <div className={`${AUDIENCE_LIST_GRID_CLASS} hidden sm:grid pb-1 text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500`}>
                                <span aria-hidden="true" />
                                <span className="text-center">Audience Group</span>
                                <span className="text-center">Leads</span>
                                <span className="text-center">Status</span>
                                <span className="text-center justify-self-center">Info</span>
                                <span className="text-center justify-self-center">Actions</span>
                            </div>
                        )}
                        {filteredAndSortedSegments.map((segment) => (
                            <CustomerGroupCard
                                key={segment.id}
                                segment={segment}
                                isSelected={selectedSegments.has(segment.id)}
                                onSelect={() => handleSelectSegment(segment.id)}
                                onEdit={() => onEditSegment(segment)}
                                onDelete={() => onDeleteSegment(segment.id)}
                                onDuplicate={() => onDuplicateSegment(segment)}
                                leads={leads}
                                onViewSourceLeads={onViewSourceLeads}
                                viewMode={viewMode}
                                onShowDetail={(segment) => setSelectedSegmentForDetail(segment)}
                                setGroupConfirmationOpen={setGroupConfirmationOpen}
                                setSelectedGroup={setSelectedGroup}
                            />
                        ))}
                    </div>
                )}
            </SkeletonSwitch>

            {selectedSegmentForDetail && (
                <SegmentDetailModal
                    segment={selectedSegmentForDetail}
                    leads={leads}
                    onClose={() => setSelectedSegmentForDetail(null)}
                    onEdit={() => { onEditSegment(selectedSegmentForDetail); setSelectedSegmentForDetail(null); }}
                    onViewSourceLeads={onViewSourceLeads}
                />
            )}

            {groupsData?.total > PAGE_SIZE && !groupsLoading && (
                <Pagination
                    totalEntries={groupsData.total}
                    entriesPerPage={PAGE_SIZE}
                    currentPage={currentPage}
                    onPageChange={(p) => { if (!groupsLoading) setCurrentPage(p); }}
                    showSummary
                    maxVisiblePages={5}
                />
            )}

            <ConfirmationDialog
                open={groupConfirmationOpen}
                title="Delete this Group?"
                description="This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={async () => { deleteCustomerGroups(selectedGroup); setGroupConfirmationOpen(false); }}
                onCancel={() => setGroupConfirmationOpen(false)}
            />
        </PageContainer>
    );
};

export default EnhancedAudiencesView;
